import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Shield, DollarSign, Users, Calendar, 
  ArrowLeft, Loader2, TrendingUp, Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { subWeeks, subMonths, startOfDay, endOfDay, isWithinInterval } from "date-fns";
import {
  AdminStats,
  AdminPendingResolutions,
  AdminEvents,
  AdminUsers,
  AdminRevenue,
  AdminResolvedEvents,
} from "@/components/admin";

interface Event {
  id: string;
  title: string;
  status: string;
  yes_pool: number;
  no_pool: number;
  total_bids: number;
  is_platform_event: boolean;
  deadline: string;
  created_at: string;
  outcome: boolean | null;
}

interface Revenue {
  id: string;
  amount: number;
  fee_type: string;
  created_at: string;
  event_id: string;
}

interface Profile {
  id: string;
  user_id: string;
  username: string | null;
  avatar_url: string | null;
  duv_coins: number;
  total_wins: number;
  total_bids: number;
  is_banned: boolean;
  created_at: string;
  email?: string;
  signup_location?: string | null;
}

interface PendingResolution {
  id: string;
  event_id: string;
  proposed_outcome: boolean;
  submitted_by: string;
  submitted_at: string;
  status: string;
  dispute_deadline?: string | null;
  dispute_count?: number;
  auto_approved?: boolean;
  escalated?: boolean;
  event?: {
    title: string;
    yes_pool: number;
    no_pool: number;
    is_platform_event: boolean;
  };
  submitter?: {
    username: string | null;
  };
  votes?: {
    agrees: number;
    disputes: number;
  };
}

const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const navigate = useNavigate();
  
  const [events, setEvents] = useState<Event[]>([]);
  const [revenue, setRevenue] = useState<Revenue[]>([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalBids, setTotalBids] = useState(0);
  const [revenueChartData, setRevenueChartData] = useState<{ date: string; amount: number }[]>([]);
  const [dateFilter, setDateFilter] = useState<"today" | "week" | "month" | "all" | "custom">("all");
  const [customDateRange, setCustomDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: undefined,
    to: undefined,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [resolvingEventId, setResolvingEventId] = useState<string | null>(null);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [reResolvingEventId, setReResolvingEventId] = useState<string | null>(null);
  
  // User management state
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [userSearch, setUserSearch] = useState("");
  const [adjustingUserId, setAdjustingUserId] = useState<string | null>(null);
  const [coinAdjustment, setCoinAdjustment] = useState<Record<string, string>>({});
  
  // Pagination state for users
  const [userPage, setUserPage] = useState(0);
  const USERS_PER_PAGE = 50;
  
  // Pending resolutions state
  const [pendingResolutions, setPendingResolutions] = useState<PendingResolution[]>([]);
  const [processingResolutionId, setProcessingResolutionId] = useState<string | null>(null);
  const [resolutionFilter, setResolutionFilter] = useState<"all" | "escalated">("all");

  // Form state for new platform event
  const [newEvent, setNewEvent] = useState({
    title: "",
    description: "",
    category: "other",
    country: "global",
    deadline: "",
  });
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!adminLoading && !isAdmin && user) {
      navigate("/");
      toast.error("Access denied. Admin only.");
    }
  }, [isAdmin, adminLoading, user, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  // Refetch when page changes
  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [userPage]);

  const fetchData = async () => {
    setIsLoading(true);

    // Fetch all events
    const { data: eventsData } = await supabase
      .from("events")
      .select("id, title, status, yes_pool, no_pool, total_bids, is_platform_event, deadline, created_at, outcome")
      .order("created_at", { ascending: false });

    if (eventsData) setEvents(eventsData);

    // Fetch revenue
    const { data: revenueData } = await supabase
      .from("platform_revenue")
      .select("*")
      .order("created_at", { ascending: false });

    if (revenueData) {
      setRevenue(revenueData);
      setTotalRevenue(revenueData.reduce((sum, r) => sum + Number(r.amount), 0));
      
      // Process revenue data for chart (group by date)
      const revenueByDate = revenueData.reduce((acc: Record<string, number>, r) => {
        const date = new Date(r.created_at).toLocaleDateString();
        acc[date] = (acc[date] || 0) + Number(r.amount);
        return acc;
      }, {});
      
      const chartData = Object.entries(revenueByDate)
        .map(([date, amount]) => ({ date, amount }))
        .slice(0, 14)
        .reverse();
      setRevenueChartData(chartData);
    }

    // Fetch total users
    const { count: usersCount } = await supabase
      .from("profiles")
      .select("*", { count: "exact", head: true });
    if (usersCount !== null) setTotalUsers(usersCount);

    // Fetch total bids
    const { count: bidsCount } = await supabase
      .from("bids")
      .select("*", { count: "exact", head: true });
    if (bidsCount !== null) setTotalBids(bidsCount);

    // Fetch profiles with pagination for user management (server-side pagination)
    const from = userPage * USERS_PER_PAGE;
    const to = from + USERS_PER_PAGE - 1;
    
    const { data: profilesData } = await supabase
      .from("profiles")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);
    
    if (profilesData) {
      // Fetch user emails for admins
      const userIds = profilesData.map((p: Profile) => p.user_id);
      try {
        const { data: emailData, error: emailError } = await supabase.functions.invoke("get-user-emails", {
          body: { user_ids: userIds }
        });
        
        if (!emailError && emailData?.emails) {
          const profilesWithEmails = profilesData.map((p: Profile) => ({
            ...p,
            email: emailData.emails[p.user_id] || undefined
          }));
          setProfiles(profilesWithEmails as Profile[]);
        } else {
          setProfiles(profilesData as Profile[]);
        }
      } catch {
        setProfiles(profilesData as Profile[]);
      }
    }

    // Fetch pending resolutions with event and submitter info
    const { data: resolutionsData } = await supabase
      .from("pending_resolutions")
      .select(`
        *,
        event:events(title, yes_pool, no_pool, is_platform_event),
        submitter:profiles!pending_resolutions_submitted_by_fkey(username)
      `)
      .eq("status", "pending")
      .order("escalated", { ascending: false })
      .order("submitted_at", { ascending: true });
    
    if (resolutionsData) {
      // Fetch votes for each resolution
      const resolutionsWithVotes = await Promise.all(
        resolutionsData.map(async (resolution) => {
          const { data: votes } = await supabase
            .from("resolution_votes")
            .select("agrees_with_outcome")
            .eq("resolution_id", resolution.id);
          
          const agrees = votes?.filter(v => v.agrees_with_outcome).length || 0;
          const disputes = votes?.filter(v => !v.agrees_with_outcome).length || 0;
          
          return {
            ...resolution,
            votes: { agrees, disputes },
          };
        })
      );
      setPendingResolutions(resolutionsWithVotes as unknown as PendingResolution[]);
    }

    setIsLoading(false);
  };

  // User management handlers
  const handleAdjustCoins = async (userId: string) => {
    const adjustment = parseFloat(coinAdjustment[userId] || "0");
    if (isNaN(adjustment) || adjustment === 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setAdjustingUserId(userId);
    try {
      const profile = profiles.find(p => p.user_id === userId);
      if (!profile) return;

      const newBalance = Math.max(0, profile.duv_coins + adjustment);
      
      const { error } = await supabase
        .from("profiles")
        .update({ duv_coins: newBalance })
        .eq("user_id", userId);

      if (error) throw error;

      toast.success(`Balance adjusted by ${adjustment > 0 ? "+" : ""}${adjustment} DC`);
      setCoinAdjustment(prev => ({ ...prev, [userId]: "" }));
      fetchData();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setAdjustingUserId(null);
    }
  };

  const handleToggleBan = async (userId: string, currentlyBanned: boolean) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ is_banned: !currentlyBanned })
        .eq("user_id", userId);

      if (error) throw error;

      toast.success(currentlyBanned ? "User unbanned" : "User banned");
      fetchData();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  // Event handlers
  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsCreating(true);

    try {
      const { error } = await supabase.from("events").insert({
        creator_id: user.id,
        title: newEvent.title,
        description: newEvent.description,
        category: newEvent.category as any,
        country: newEvent.country as any,
        deadline: newEvent.deadline,
        is_platform_event: true,
      } as any);

      if (error) throw error;

      toast.success("Platform event created!");
      setNewEvent({
        title: "",
        description: "",
        category: "other",
        country: "global",
        deadline: "",
      });
      fetchData();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleResolve = async (eventId: string, outcome: boolean) => {
    setResolvingEventId(eventId);

    try {
      const { data, error } = await supabase.functions.invoke("resolve-event", {
        body: { event_id: eventId, outcome },
      });

      if (error) throw error;

      toast.success(`Event resolved! ${data.winners_count} winners, Platform fee: ${data.platform_fee.toFixed(2)} DC`);
      fetchData();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setResolvingEventId(null);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!confirm("Are you sure you want to delete this event? This action cannot be undone.")) {
      return;
    }

    setDeletingEventId(eventId);

    try {
      await supabase.from("bids").delete().eq("event_id", eventId);
      await supabase.from("pending_resolutions").delete().eq("event_id", eventId);
      await supabase.from("event_invites").delete().eq("event_id", eventId);
      await supabase.from("event_participants").delete().eq("event_id", eventId);

      const { error } = await supabase.from("events").delete().eq("id", eventId);

      if (error) throw error;

      toast.success("Event deleted successfully");
      fetchData();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setDeletingEventId(null);
    }
  };

  const handleReResolve = async (eventId: string, newOutcome: boolean) => {
    if (!confirm(`Are you sure you want to change the outcome to ${newOutcome ? "YES" : "NO"}? This will redistribute all coins.`)) {
      return;
    }

    setReResolvingEventId(eventId);

    try {
      const { data, error } = await supabase.functions.invoke("re-resolve-event", {
        body: { event_id: eventId, new_outcome: newOutcome },
      });

      if (error) throw error;

      toast.success(`Event re-resolved! ${data.new_winners_count} new winners, ${data.previous_winners_reversed} previous winners reversed`);
      fetchData();
    } catch (error: any) {
      toast.error(error.message || "Failed to re-resolve event");
    } finally {
      setReResolvingEventId(null);
    }
  };

  // Resolution handlers
  const handleApproveResolution = async (resolution: PendingResolution) => {
    setProcessingResolutionId(resolution.id);

    try {
      const { data, error } = await supabase.functions.invoke("resolve-event", {
        body: { event_id: resolution.event_id, outcome: resolution.proposed_outcome },
      });

      if (error) throw error;

      await supabase
        .from("pending_resolutions")
        .update({ 
          status: "approved", 
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString()
        })
        .eq("id", resolution.id);

      toast.success(`Resolution approved! ${data.winners_count} winners`);
      fetchData();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setProcessingResolutionId(null);
    }
  };

  const handleRejectResolution = async (resolutionId: string) => {
    setProcessingResolutionId(resolutionId);

    try {
      const { error } = await supabase
        .from("pending_resolutions")
        .update({ 
          status: "rejected", 
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString()
        })
        .eq("id", resolutionId);

      if (error) throw error;

      toast.success("Resolution rejected");
      fetchData();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setProcessingResolutionId(null);
    }
  };

  // Filter displayed profiles by search
  const filteredProfiles = profiles.filter(p => 
    p.username?.toLowerCase().includes(userSearch.toLowerCase()) ||
    p.user_id.toLowerCase().includes(userSearch.toLowerCase()) ||
    p.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  // Filter revenue by date range
  const filteredRevenue = useMemo(() => {
    if (dateFilter === "all") return revenue;
    
    const now = new Date();
    let startDate: Date;
    let endDate = endOfDay(now);
    
    switch (dateFilter) {
      case "today":
        startDate = startOfDay(now);
        break;
      case "week":
        startDate = startOfDay(subWeeks(now, 1));
        break;
      case "month":
        startDate = startOfDay(subMonths(now, 1));
        break;
      case "custom":
        if (!customDateRange.from) return revenue;
        startDate = startOfDay(customDateRange.from);
        endDate = customDateRange.to ? endOfDay(customDateRange.to) : endOfDay(now);
        break;
      default:
        return revenue;
    }
    
    return revenue.filter(r => {
      const date = new Date(r.created_at);
      return isWithinInterval(date, { start: startDate, end: endDate });
    });
  }, [revenue, dateFilter, customDateRange]);

  const filteredTotalRevenue = useMemo(() => {
    return filteredRevenue.reduce((sum, r) => sum + Number(r.amount), 0);
  }, [filteredRevenue]);

  const filteredRevenueByType = useMemo(() => {
    return filteredRevenue.reduce((acc: Record<string, number>, r) => {
      acc[r.fee_type] = (acc[r.fee_type] || 0) + Number(r.amount);
      return acc;
    }, {});
  }, [filteredRevenue]);

  const resolvedEvents = events.filter(e => e.status === "RESOLVED");

  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" onClick={() => navigate("/")}>
              <ArrowLeft size={18} />
            </Button>
            <div className="flex items-center gap-3">
              <Shield size={32} className="text-primary" />
              <h1 className="text-3xl font-bold text-foreground">Admin Panel</h1>
            </div>
          </div>

          {/* Stats Cards */}
          <AdminStats
            totalRevenue={totalRevenue}
            totalUsers={totalUsers}
            totalBids={totalBids}
            eventsCount={events.length}
          />

          {/* Revenue Chart */}
          {revenueChartData.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 mb-8"
            >
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <TrendingUp size={20} className="text-primary" />
                Revenue Over Time
              </h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueChartData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="date" 
                      stroke="hsl(var(--muted-foreground))" 
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))" 
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => `${value} DC`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: "hsl(var(--card))", 
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        color: "hsl(var(--foreground))"
                      }}
                      formatter={(value: number) => [`${value.toFixed(2)} DC`, "Revenue"]}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="amount" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorRevenue)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          )}

          {/* Tabs for different sections */}
          <Tabs defaultValue="pending" className="space-y-6">
            <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
              <TabsTrigger value="pending" className="relative">
                Pending
                {pendingResolutions.length > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-destructive text-destructive-foreground rounded-full">
                    {pendingResolutions.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="events">Events</TabsTrigger>
              <TabsTrigger value="resolved" className="relative">
                Resolved
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-muted text-muted-foreground rounded-full">
                  {resolvedEvents.length}
                </span>
              </TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="revenue">Revenue</TabsTrigger>
            </TabsList>

            <TabsContent value="pending">
              <AdminPendingResolutions
                pendingResolutions={pendingResolutions}
                isLoading={isLoading}
                processingResolutionId={processingResolutionId}
                resolutionFilter={resolutionFilter}
                setResolutionFilter={setResolutionFilter}
                onApprove={handleApproveResolution}
                onReject={handleRejectResolution}
              />
            </TabsContent>

            <TabsContent value="events">
              <AdminEvents
                events={events}
                isLoading={isLoading}
                isCreating={isCreating}
                resolvingEventId={resolvingEventId}
                deletingEventId={deletingEventId}
                newEvent={newEvent}
                setNewEvent={setNewEvent}
                onCreateEvent={handleCreateEvent}
                onResolve={handleResolve}
                onDelete={handleDeleteEvent}
              />
            </TabsContent>

            <TabsContent value="resolved">
              <AdminResolvedEvents
                resolvedEvents={resolvedEvents}
                isLoading={isLoading}
                reResolvingEventId={reResolvingEventId}
                onReResolve={handleReResolve}
              />
            </TabsContent>

            <TabsContent value="users">
              <AdminUsers
                profiles={filteredProfiles}
                totalUsers={totalUsers}
                isLoading={isLoading}
                userSearch={userSearch}
                setUserSearch={setUserSearch}
                userPage={userPage}
                setUserPage={setUserPage}
                usersPerPage={USERS_PER_PAGE}
                adjustingUserId={adjustingUserId}
                coinAdjustment={coinAdjustment}
                setCoinAdjustment={setCoinAdjustment}
                onAdjustCoins={handleAdjustCoins}
                onToggleBan={handleToggleBan}
              />
            </TabsContent>

            <TabsContent value="revenue">
              <AdminRevenue
                filteredRevenue={filteredRevenue}
                filteredTotalRevenue={filteredTotalRevenue}
                filteredRevenueByType={filteredRevenueByType}
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                customDateRange={customDateRange}
                setCustomDateRange={setCustomDateRange}
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default Admin;
